import React, { useEffect, useState } from 'react';
import { View, StyleSheet, useWindowDimensions } from 'react-native';
import ToolTip from './ToolTip';
import { useTooltipSequence } from './useToolTipSequence';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSignedInContext } from '../../../context/SignedInContext';

type TooltipSequenceProps = {
  stage: number;
  onNext?: () => void;
  onClose?: () => void;
  backgroundColor?: string;
  borderWidth?: number;
  borderColor?: string;
};

export const TooltipSequence = ({
  stage,
  onNext,
  onClose,
  backgroundColor,
  borderWidth,
  borderColor,
}: TooltipSequenceProps) => {
  const { stepIndex, setStage, currentStep, isFinished, next, close } = useTooltipSequence();
  const { width, height } = useWindowDimensions();
  const [isVisible, setIsVisible] = useState(true);
  const pathSizeLimit = width < 1251;
  useEffect(() => {
    (async () => {
      try {
        const savedStage = await AsyncStorage.getItem('tooltipStage');
        if (savedStage && Number(savedStage) >= stage) {
          setIsVisible(false);
        }
      } catch (e) {
        console.warn('Failed to load tooltip stage', e);
      }
    })();
  }, [stage]);

  const { user } = useSignedInContext();

  // Set current stage
  React.useEffect(() => {
    setStage(stage);
  }, [stage, setStage]);

  // 🔹 Hide tooltip if not active stage, finished, or missing layout
  if (!isVisible || isFinished || !currentStep || stepIndex !== stage) return null;

  // if (!currentStep || stepIndex !== stage || isFinished) return null;

  // Use simple relative positions from tooltipSteps
  const top = pathSizeLimit
    ? currentStep.positionMobile?.top ?? height / 2 - 50
    : currentStep.position?.top ?? height / 2 - 50; // default center
  const left = pathSizeLimit
    ? currentStep.positionMobile?.left ?? width / 2 - 100
    : currentStep.position?.left ?? width / 2 - 100; // default center

  return (
    <View
      style={[
        StyleSheet.absoluteFill,
        styles.overlay,
        backgroundColor ? { backgroundColor } : undefined,
        borderWidth ? { borderWidth: borderWidth } : undefined,
        borderColor ? { borderColor: borderColor } : undefined,
      ]}
    >
      <ToolTip
        name={user?.firstName ?? ''}
        lines={currentStep.lines}
        buttonText={currentStep.buttonText}
        icon={currentStep.icon}
        position={{ top, left }}
        onPress={async () => {
          try {
            await AsyncStorage.setItem('tooltipStage', String(stage)); // ✅ Save progress
          } catch (e) {
            console.warn('Failed to save tooltip stage', e);
          }
          next();
          onNext?.();
        }}
        onClose={async () => {
          try {
            await AsyncStorage.setItem('tooltipStage', String(stage)); // ✅ Save even if closed
          } catch (e) {
            console.warn('Failed to save tooltip stage', e);
          }
          close();
          onClose?.();
        }}
        showHeader={currentStep.showHeader}
        arrow={pathSizeLimit ? currentStep.arrowMobile : currentStep.arrow}
        arrowPosition={pathSizeLimit ? currentStep.arrowPositionMobile : currentStep.arrowPosition}
        footerText={currentStep.footerText}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    // backgroundColor: 'rgba(0,0,0,0.25)', // dim background
    // backgroundColor: 'rgba(227, 234, 29, 0.25)', // dim background
  },
});
